/**
 * Scheduled Scans API module: CRUD over /api/scheduled-scans.
 *
 * Types here mirror packages/server/src/services/scheduledScanRows.ts
 * (ScheduledScanRow) and packages/server/src/services/scanCadence.ts
 * (ScanType, ScanCadence, ScheduleSource) field for field. The server package
 * is not a dependency of the dashboard, so these are a deliberate local
 * mirror rather than a shared import, the same pattern as BetaInviteLookup in
 * betaInvites.ts.
 *
 * Spec: docs/superpowers/specs/2026-08-05-scheduled-scans-design.md (API).
 */
import { fetchApiEnvelope, fetchApi } from './client';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';
import { log } from '@/lib/logger';

log.info('[api/scheduledScans] Module loaded');

export type ScanTypeValue = 'accessibility' | 'contrast' | 'tokens' | 'auto_review';
export type CadenceValue = 'daily' | 'weekly' | 'monthly';
export type ScheduleSourceValue = 'onboarding' | 'dashboard';
export type ScheduleStatus = 'active' | 'paused' | 'paused_unreachable' | 'paused_bot_protection' | 'paused_page_target' | 'paused_snapshot_limit';

/** The one row shape every read path (GET, POST, PATCH) serializes through. */
export interface ScheduledScanRow {
  id: string;
  url: string;
  projectId: string;
  projectName: string | null;
  scanType: ScanTypeValue;
  cadence: CadenceValue;
  source: ScheduleSourceValue;
  status: ScheduleStatus;
  lastScannedAt: string | null;
  nextRunAt: string | null;
  lastScore: number | null;
  lastShareUrl: string | null;
  lastDeltaAdded: number | null;
  lastDeltaFixed: number | null;
  lastFindingCount: number;
  /**
   * Whether the last scan stored the DOM it measured, so a review can render
   * that render as its canvas and pin the findings where they happened.
   * A BOOLEAN, not the URL: the dashboard never needs the bytes, and shipping
   * a storage URL to the browser would be a needless widening.
   */
  hasStoredCanvas?: boolean;
}

export interface CreateScheduledScanRequest {
  confirmLoginPage?: boolean;
  projectId: string;
  url: string;
  scanType: ScanTypeValue;
  cadence: CadenceValue;
}

/** PATCH body: exactly one of a cadence change or a pause/resume action. */
export type UpdateScheduledScanRequest =
  | { cadence: CadenceValue }
  | { action: 'pause' | 'resume' };

export const scheduledScansApi = {
  list: (token: string | undefined): Promise<ScheduledScanRow[]> => {
    log.info('[api/scheduledScans] list');
    // Bounded: SchedulePage spins on this one request and reaches its LoadError
    // only on rejection, so an unbounded stall is a permanent loader. `run`
    // below is deliberately NOT bounded — it launches a real browser.
    return fetchApi<ScheduledScanRow[]>('/scheduled-scans', {
      token,
      timeoutMs: SCREEN_READ_TIMEOUT_MS,
    });
  },

  create: (
    token: string | undefined,
    data: CreateScheduledScanRequest,
  ): Promise<ScheduledScanRow> => {
    log.info('[api/scheduledScans] create', { projectId: data.projectId, scanType: data.scanType });
    return fetchApi<ScheduledScanRow>('/scheduled-scans', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    });
  },

  update: (
    token: string | undefined,
    id: string,
    data: UpdateScheduledScanRequest,
  ): Promise<ScheduledScanRow> => {
    log.info('[api/scheduledScans] update', { id });
    return fetchApi<ScheduledScanRow>(`/scheduled-scans/${id}`, {
      token,
      method: 'PATCH',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    });
  },

  /**
   * Scan this schedule right now. Returns the refreshed row, so the caller can
   * render the new result without waiting for a refetch.
   *
   * Never emails; the server decides that (runScheduledScanNow). A failure
   * carries the server's own sentence, which is the whole reason the button
   * exists, so callers must surface it rather than swallowing it.
   *
   * Every refusal here is a HANDLED outcome this caller renders, so all four
   * are opted in via expectedErrorStatuses and stay off console.error, which
   * PostHog's capture_console_errors would otherwise file as a fake $exception
   * (the PR #77/#79 class). 400 = we could not reach the site, 404 = the
   * schedule was deleted mid-scan, 409 = it is paused, 503 = OUR scanner could
   * not run (added 2026-09-08 with the failure-attribution fix).
   */
  run: (token: string | undefined, id: string): Promise<ScheduledScanRow> => {
    log.info('[api/scheduledScans] run', { id });
    return fetchApi<ScheduledScanRow>(`/scheduled-scans/${id}/run`, {
      token,
      method: 'POST',
      expectedErrorStatuses: [400, 404, 409, 503],
    });
  },

  /**
   * Open a team review on the scan's OWN canvas.
   *
   * Reads the full envelope, not the unwrapped body: liveAvailable and
   * authHandoff are siblings of `data` (the createLiveReview pattern), and the
   * handoff is what makes the review render authenticated at the api origin
   * where the dashboard's Clerk cookie may not reach.
   *
   * Deliberately unbounded, like run: the server does no capture here, but a
   * cold start plus the insert is still a real request and a deadline would
   * only turn a slow one into a failed one.
   */
  reviewFindings: async (
    token: string | undefined,
    id: string,
  ): Promise<{ shareUrl: string; liveAvailable: boolean; authHandoff?: string }> => {
    log.info('[api/scheduledScans] reviewFindings', { id });
    const envelope = await fetchApiEnvelope<{ shareUrl: string; reviewId: string }>(
      `/scheduled-scans/${id}/review`,
      { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS },
    );
    return {
      shareUrl: envelope.data.shareUrl,
      liveAvailable: envelope.liveAvailable === true,
      authHandoff: typeof envelope.authHandoff === 'string' ? envelope.authHandoff : undefined,
    };
  },

  delete: (token: string | undefined, id: string): Promise<{ id: string }> => {
    log.info('[api/scheduledScans] delete', { id });
    return fetchApi<{ id: string }>(`/scheduled-scans/${id}`, {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    });
  },
};
